import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Department-wise Projects Chart
 * Horizontal grouped bar chart showing Projects and Avg Duration by department
 */
const MBFDepartmentProjectsChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Default data matching the Figma design exactly
    const defaultData = [
        { department: 'CSE', projects: 90, avgDuration: 15 },
        { department: 'ME', projects: 55, avgDuration: 12 },
        { department: 'EE', projects: 25, avgDuration: 20 },
        { department: 'ECE', projects: 55, avgDuration: 10 },
        { department: 'Civil', projects: 70, avgDuration: 18 },
        { department: 'Chemical', projects: 10, avgDuration: 5 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.department || item.label);
    const projects = chartData.map(item => item.projects || 0);
    // Handle both camelCase and snake_case field names
    const avgDuration = chartData.map(item => item.avgDuration || item.avg_duration_weeks || 0);

    // Calculate dynamic X-axis max (based on max of projects values)
    const maxValue = Math.max(...projects, 10);
    const stepSize = Math.ceil(maxValue / 4 / 25) * 25 || 25;
    const xAxisMax = Math.ceil(maxValue * 1.1 / stepSize) * stepSize;

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;
            const barThickness = isMobile ? 8 : 12;

            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Projects',
                            data: projects,
                            backgroundColor: '#b8d4f0',
                            borderColor: '#8bb8e8',
                            borderWidth: 1,
                            borderRadius: 2,
                            barThickness: barThickness,
                        },
                        {
                            label: 'Avg. Duration (In Weeks)',
                            data: avgDuration,
                            backgroundColor: '#f0b8d4',
                            borderColor: '#e88bb8',
                            borderWidth: 1,
                            borderRadius: 2,
                            barThickness: barThickness,
                        },
                    ],
                },
                options: {
                    indexAxis: 'y', // Horizontal bar chart
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                                boxWidth: 12,
                                boxHeight: 12,
                                padding: 15,
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: 11,
                                },
                                color: '#333333',
                            },
                        },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                        },
                        datalabels: { display: false },
                    },
                    scales: {
                        x: {
                            min: 0,
                            max: xAxisMax,
                            ticks: {
                                stepSize: stepSize,
                                color: '#666666',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                            title: {
                                display: true,
                                text: 'Count / Duration',
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: 12,
                                    weight: '500',
                                },
                            },
                        },
                        y: {
                            grid: { display: false },
                            border: { display: false },
                            ticks: {
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                            title: {
                                display: true,
                                text: 'No. of Users',
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: 12,
                                    weight: '500',
                                },
                            },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, chartData, labels, projects, avgDuration, xAxisMax, stepSize]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Department-wise Projects
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Chart Area */}
            <Box
                sx={{
                    flex: 1,
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    bgcolor: '#fafafa',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e6e6e6',
                        padding: '16px',
                        minHeight: 0,
                    }}
                >
                    <canvas ref={chartRef} />
                </Box>
            </Box>
        </Box>
    );
};

export default MBFDepartmentProjectsChart;
